import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Mail, Phone, MapPin, Send, CheckCircle, Home, MessageSquare } from "lucide-react";
import { branding } from "@/config/branding";
import type { ContactSubmission, JoinTeamForm } from "@/types";

const inputClasses =
  "w-full px-4 py-3 bg-willard-grey-900 border border-willard-grey-700 rounded-lg text-white placeholder-willard-grey-500 focus:outline-none focus:ring-2 focus:ring-willard-taupe focus:border-transparent";

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState<JoinTeamForm>({
    name: "",
    email: "",
    phone: "",
    grade: "",
    experience: "none",
    usbcId: "",
    league: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const submission: Omit<ContactSubmission, "id"> = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: `Join Team Inquiry - Grade ${formData.grade}`,
        message: `Experience Level: ${formData.experience}\n\n${formData.message}`,
        type: "join-team",
        status: "new",
        submittedAt: new Date(),
        // Structured intake — lets the coach create the player card straight
        // from the request; the USBC # is what connects their league record.
        grade: formData.grade,
        experience: formData.experience,
        ...(formData.usbcId.trim() ? { usbcId: formData.usbcId.trim() } : {}),
        ...(formData.league.trim() ? { league: formData.league.trim() } : {}),
      };

      await addDoc(collection(db, "contactSubmissions"), {
        ...submission,
        submittedAt: serverTimestamp(),
      });

      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        grade: "",
        experience: "none",
        usbcId: "",
        league: "",
        message: "",
      });
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(
        "Failed to submit form. Please try again or contact us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-willard-black text-white min-h-screen">
      <div className="max-w-6xl mx-auto px-5 py-14">
        {/* Page header */}
        <header className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-willard-taupe mb-2">
            Contact &amp; Recruitment
          </p>
          <h1 className="font-display text-5xl sm:text-6xl mb-3">JOIN THE TIGERS</h1>
          <p className="text-willard-grey-400 max-w-2xl">
            Prospective players, parents, or anyone curious about the program: reach out.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 items-start">
          {/* LEFT - INFO */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-1">
              <MessageSquare className="w-6 h-6 text-willard-taupe" />
              <h2 className="font-heading text-3xl tracking-wide uppercase">Get In Touch</h2>
            </div>

            <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-willard-taupe flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-1">
                    Location
                  </p>
                  <p className="text-willard-grey-200 text-sm font-bold">{branding.homeCenter.name}</p>
                  <p className="text-willard-grey-400 text-sm">{branding.homeCenter.address}</p>
                </div>
              </div>
            </div>

            {branding.contact.coach.phone && (
              <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5 hover:border-willard-taupe transition-colors">
                <a
                  href={`tel:${branding.contact.coach.phone}`}
                  className="flex items-start gap-3"
                >
                  <Phone className="w-5 h-5 text-willard-taupe flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-1">
                      Phone
                    </p>
                    <p className="text-willard-grey-400 text-sm">
                      {branding.contact.coach.phone}
                    </p>
                  </div>
                </a>
              </div>
            )}

            {branding.contact.coach.email && (
              <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5 hover:border-willard-taupe transition-colors">
                <a
                  href={`mailto:${branding.contact.coach.email}`}
                  className="flex items-start gap-3"
                >
                  <Mail className="w-5 h-5 text-willard-taupe flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-1">
                      Email
                    </p>
                    <p className="text-willard-grey-400 text-sm break-all">
                      {branding.contact.coach.email}
                    </p>
                  </div>
                </a>
              </div>
            )}

            <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2">
                Practice Schedule
              </p>
              <p className="text-willard-grey-400 text-sm mb-1">
                Monday: 3:30 PM - 5:30 PM
              </p>
              <p className="text-willard-grey-400 text-sm mb-2">
                Wednesday: 3:30 PM - 5:30 PM
              </p>
              <p className="text-willard-grey-500 text-sm">
                All skill levels welcome. Equipment available for beginners.
              </p>
            </div>

            <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2">
                Recruiters &amp; Coaches
              </p>
              <p className="text-willard-grey-400 text-sm">
                College recruiter and coach inquiries route through Coach{" "}
                {branding.contact.coach.name}. Use the phone or email above.
              </p>
            </div>
          </div>

          {/* RIGHT - FORM */}
          <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5 sm:p-6">
            {submitted ? (
              <div className="text-center py-10">
                <CheckCircle className="w-12 h-12 text-willard-taupe mx-auto mb-4" />
                <h3 className="font-heading text-3xl tracking-wide uppercase mb-2">
                  Thank You
                </h3>
                <p className="text-willard-grey-400 mb-6">
                  We received your inquiry and will get back to you soon.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <button
                    onClick={() => setSubmitted(false)}
                    className="bg-willard-taupe text-willard-black font-bold rounded-lg px-6 py-3 hover:bg-white transition-colors"
                  >
                    Submit Another Inquiry
                  </button>
                  <button
                    onClick={() => (window.location.href = "/")}
                    className="border border-willard-grey-700 rounded-lg px-6 py-3 hover:border-willard-taupe transition-colors flex items-center justify-center gap-2 text-willard-grey-400"
                  >
                    <Home className="w-5 h-5" /> Back to Home
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-3xl tracking-wide uppercase mb-6">
                  Join the Team
                </h2>

                {error && (
                  <div className="mb-6 p-4 bg-willard-grey-800 border border-red-500 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {[
                    { id: "name", label: "Full Name *", type: "text" },
                    { id: "email", label: "Email Address *", type: "email" },
                    { id: "phone", label: "Phone Number", type: "tel" },
                  ].map((field) => (
                    <div key={field.id}>
                      <label
                        htmlFor={field.id}
                        className="block text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2"
                      >
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        id={field.id}
                        name={field.id}
                        value={(formData as any)[field.id]}
                        onChange={handleChange}
                        required={field.label.includes("*")}
                        className={inputClasses}
                        placeholder={`Enter your ${field.id}`}
                      />
                    </div>
                  ))}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="grade"
                        className="block text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2"
                      >
                        Current Grade *
                      </label>
                      <select
                        id="grade"
                        name="grade"
                        value={formData.grade}
                        onChange={handleChange}
                        required
                        className={inputClasses}
                      >
                        <option value="">Select grade</option>
                        <option value="9">9th Grade</option>
                        <option value="10">10th Grade</option>
                        <option value="11">11th Grade</option>
                        <option value="12">12th Grade</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="experience"
                        className="block text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2"
                      >
                        Bowling Experience *
                      </label>
                      <select
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        required
                        className={inputClasses}
                      >
                        <option value="none">No Experience</option>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="usbcId"
                        className="block text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2"
                      >
                        USBC Member ID
                      </label>
                      <input
                        type="text"
                        id="usbcId"
                        name="usbcId"
                        value={formData.usbcId}
                        onChange={handleChange}
                        className={inputClasses}
                        placeholder="23-9833"
                      />
                      <p className="text-xs text-willard-grey-500 mt-1.5">
                        On your USBC card or bowl.com, it's how we pull your league averages and
                        honors. Leave blank if you've never bowled in a league; we'll get you one.
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="league"
                        className="block text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2"
                      >
                        Current League / Center
                      </label>
                      <input
                        type="text"
                        id="league"
                        name="league"
                        value={formData.league}
                        onChange={handleChange}
                        className={inputClasses}
                        placeholder="e.g. Junior Majors at Sunshine Lanes"
                      />
                      <p className="text-xs text-willard-grey-500 mt-1.5">
                        Where do you bowl now, if anywhere? Helps us find your stats.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className={`${inputClasses} resize-none`}
                      placeholder="Tell us about yourself..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-willard-taupe text-willard-black font-bold rounded-lg px-6 py-3 hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-willard-black mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" /> Submit Inquiry
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
